import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './PaymentForm.css';

// Initialize Stripe with live key
const stripePromise = loadStripe('pk_live_51RtVDiIV8d6YCkKgxv1nxOuu4zHPK857KTOzxF7NP4fKQeSlPlPBTDyb1sBD23wVMGBmg3yrn0nnVJcaG46iQA1U00gtxXAg83');

const PaymentFormComponent = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [amount, setAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setMessage('Please enter a valid amount');
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      // Execute reCAPTCHA
      if (!executeRecaptcha) {
        setMessage('reCAPTCHA not available. Please refresh the page and try again.');
        setIsProcessing(false);
        return;
      }

      const recaptchaToken = await executeRecaptcha('payment');
      
      if (!recaptchaToken) {
        setMessage('reCAPTCHA verification failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      const cardElement = elements.getElement(CardElement);

      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setMessage(error.message);
        setIsProcessing(false);
        return;
      }

      // Send payment to backend for processing
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const response = await fetch(`${API_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          paymentMethodId: paymentMethod.id,
          recaptchaToken: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.error || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Check if additional action is required (e.g., 3D Secure)
      if (data.requiresAction && data.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
        
        if (confirmError) {
          setMessage(confirmError.message);
          setIsProcessing(false);
          return;
        }

        // Verify payment was successful
        const confirmResponse = await fetch(`${API_URL}/api/confirm-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentIntentId: data.paymentIntentId,
          }),
        });

        const confirmData = await confirmResponse.json();

        if (!confirmResponse.ok || !confirmData.success) {
          setMessage(confirmData.error || 'Payment confirmation failed. Please try again.');
          setIsProcessing(false);
          return;
        }
      }

      // Payment successful
      setMessage('Payment successful! Thank you for your payment.');
      setIsSuccess(true);
      setAmount('');
      
      // Reset form after success
      setTimeout(() => {
        setMessage('');
        setIsSuccess(false);
      }, 5000);

    } catch (err) {
      setMessage('An unexpected error occurred. Please try again.');
    }

    setIsProcessing(false);
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#324B34',
        fontFamily: 'Press Start 2P, cursive',
        '::placeholder': {
          color: '#7CB980',
        },
      },
      invalid: {
        color: '#A8524C',
      },
    },
  };

  return (
    <div className="payment-form-container">
      <h2>Payment Portal</h2>
      <p className="payment-description">
        Enter your payment amount and card information below.
      </p>
      
      <form onSubmit={handleSubmit} className="payment-form">
        <div className="form-group">
          <label htmlFor="amount">Payment Amount ($)</label>
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            min="0.01"
            step="0.01"
            required
            disabled={isProcessing}
          />
        </div>

        <div className="form-group">
          <label>Card Information</label>
          <div className="card-element-container">
            <CardElement options={cardElementOptions} />
          </div>
        </div>

        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className={`btn btn-primary btn-large payment-button ${isProcessing ? 'processing' : ''}`}
        >
          {isProcessing ? 'Processing...' : `Pay $${amount || '0.00'}`}
        </button>

        {message && (
          <div className={`payment-message ${isSuccess ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </form>
    </div>
  );
};

const PaymentForm = () => {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey="6LcAO-QrAAAAAH8zXiiJd8EaNfIL1T2HKzBlIAFo"
      scriptProps={{
        async: false,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <Elements stripe={stripePromise}>
        <PaymentFormComponent />
      </Elements>
    </GoogleReCaptchaProvider>
  );
};

export default PaymentForm;
